// Fish archetype (MOB-2, M18-adjacent). PURE (ENG-1).
//
// Cod, salmon and tropical fish shipped on the `ambient` archetype — the bat's
// cave-flutter — which drifts in 3D with no regard for water or for each other. A
// fish should do two things a bat does not: stay in the water, and school. It also
// bolts when something large gets close.
//
// Steering is the classic three-rule flock, but only two rules matter at this
// scale: cohesion toward the centre of the school and separation from the fish you
// are about to bump into. Alignment is skipped deliberately — the wander heading
// already does that job, and a third rule at these speeds just reads as jitter.
import { FISH } from '../../config';
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext, MobWorld } from '../entity';

/** A cell that a fish can swim in. */
function swimmable(world: MobWorld, x: number, y: number, z: number): boolean {
  return world.isFluid(Math.floor(x), Math.floor(y), Math.floor(z));
}

/** The centre of the school around `e`, and the nearest schoolmate's offset. */
export interface School {
  count: number;
  cx: number;
  cz: number;
  /** Offset to the nearest schoolmate (for separation), or null if alone. */
  nearest: { dx: number; dz: number; dist: number } | null;
}

/** Gather the same-species fish within SCHOOL_RANGE of `e`. Pure over a list. */
export function surveySchool(e: Entity, others: readonly Entity[]): School {
  let count = 0, sx = 0, sz = 0;
  let nearest: School['nearest'] = null;
  for (const o of others) {
    if (o === e || o.removed || o.type !== e.type) continue;
    const dx = o.x - e.x, dz = o.z - e.z;
    const dist = Math.hypot(dx, dz);
    if (dist > FISH.SCHOOL_RANGE) continue;
    count++; sx += o.x; sz += o.z;
    if (!nearest || dist < nearest.dist) nearest = { dx, dz, dist };
  }
  return { count, cx: count ? sx / count : e.x, cz: count ? sz / count : e.z, nearest };
}

/** The steering a fish applies this tick, before speed scaling. Pure + testable. */
export function schoolSteer(e: Entity, school: School, flee: { dx: number; dz: number; dist: number } | null): { x: number; z: number } {
  let x = 0, z = 0;
  // Flee dominates: a threat inside FLEE_RANGE overrides the school entirely.
  if (flee && flee.dist < FISH.FLEE_RANGE && flee.dist > 0) {
    return { x: -flee.dx / flee.dist, z: -flee.dz / flee.dist };
  }
  if (school.count > 0) {
    const cdx = school.cx - e.x, cdz = school.cz - e.z;
    const cd = Math.hypot(cdx, cdz);
    if (cd > FISH.COHESION_DEADZONE) { x += (cdx / cd) * FISH.COHESION; z += (cdz / cd) * FISH.COHESION; }
    const n = school.nearest;
    if (n && n.dist > 0 && n.dist < FISH.SEPARATION_RANGE) {
      x -= (n.dx / n.dist) * FISH.SEPARATION;
      z -= (n.dz / n.dist) * FISH.SEPARATION;
    }
  }
  return { x, z };
}

export function fish(e: Entity, def: MobDef, ctx: MobContext): void {
  const { world, sys, frame } = ctx;
  // Re-pick a drifting heading periodically.
  if (--e.wanderTimer <= 0) {
    e.wanderTimer = FISH.WANDER_INTERVAL + ctx.rng.nextInt(FISH.WANDER_INTERVAL);
    e.wanderAngle = ctx.rng.nextFloat() * Math.PI * 2;
    e.data.wishV = (ctx.rng.nextFloat() - 0.5) * 0.5;
  }
  const speed = (def.speed / 20) * FISH.SPEED_SCALE;

  // A nearby player is a threat worth bolting from.
  const near = sys.nearestPlayer(frame, e.x, e.y, e.z, FISH.FLEE_RANGE);
  const pl = near ? frame.players[near.idx] : null;
  const flee = pl ? { dx: pl.x - e.x, dz: pl.z - e.z, dist: Math.hypot(pl.x - e.x, pl.z - e.z) } : null;

  const steer = schoolSteer(e, surveySchool(e, sys.entities), flee);
  const wanderX = Math.cos(e.wanderAngle), wanderZ = Math.sin(e.wanderAngle);
  const wx = wanderX * FISH.WANDER_WEIGHT + steer.x;
  const wz = wanderZ * FISH.WANDER_WEIGHT + steer.z;
  const mag = Math.hypot(wx, wz) || 1;
  e.wishX = (wx / mag) * speed;
  e.wishZ = (wz / mag) * speed;

  // Stay in the water: sink away from air above, rise off the seabed, else drift.
  let vy = (e.data.wishV ?? 0) * speed;
  if (!swimmable(world, e.x, e.y + 1, e.z)) vy = -speed; // surface ahead → dive
  else if (!swimmable(world, e.x, e.y - 1, e.z)) vy = speed; // floor below → rise
  e.wishY = vy;
  e.yaw = Math.atan2(-e.wishX, -e.wishZ);
}
